// Vouchers API layer.
//
// READs are wrapped in `withFallback` so a missing backend endpoint falls
// through to a typed empty payload. Mutations (create, deactivate, delete)
// are NOT wrapped — failures must reach the UI as toast errors.

import { apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';
import type {
  Voucher,
  VoucherCreateInput,
  VoucherListParams,
  VoucherListResult,
  VoucherRedemptionList,
  VoucherStats,
} from './types';

/**
 * Backend endpoints referenced by the Voucher feature that are not yet
 * implemented in services/api. Keep in sync with any gap-analysis docs.
 */
export const BACKEND_GAPS = [
  'GET    /api/v1/vouchers',
  'GET    /api/v1/vouchers/:id',
  'POST   /api/v1/vouchers',
  'PUT    /api/v1/vouchers/:id/deactivate',
  'DELETE /api/v1/vouchers/:id',
  'GET    /api/v1/vouchers/:id/redemptions',
  'GET    /api/v1/vouchers/:id/stats',
] as const;

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&')}`;
}

// ─── Empty fallbacks ─────────────────────────────────────────────────────────

const emptyList: VoucherListResult = {
  data: [],
  meta: { page: 1, limit: 20, total: 0, totalPages: 0 },
};

const emptyStats: VoucherStats = {
  total_redemptions: 0,
  total_discount_given: 0,
  unique_customers: 0,
  utilisation_pct: null,
  days_remaining: null,
};

const emptyRedemptions: VoucherRedemptionList = {
  data: [],
  meta: { page: 1, limit: 20, total: 0, totalPages: 0 },
};

// ─── API ─────────────────────────────────────────────────────────────────────

export const vouchersApi = {
  async list(params: VoucherListParams): Promise<VoucherListResult> {
    return withFallback(
      async () => {
        const res = await apiClientPaginated<Voucher[]>(
          `/vouchers${buildQuery({
            page: params.page ?? 1,
            limit: params.limit ?? 20,
            search: params.search,
            status: params.status,
            type: params.type,
          })}`,
        );
        return { data: res.data, meta: res.meta };
      },
      emptyList,
    );
  },

  async get(id: string): Promise<Voucher | null> {
    return withFallback(() => apiClient<Voucher>(`/vouchers/${id}`), null);
  },

  /**
   * Create a voucher. Surfaces CRM_VOUCHER_CODE_DUPLICATE from the backend
   * as a real error so the caller can map it onto the code input.
   */
  async create(input: VoucherCreateInput): Promise<Voucher> {
    return apiClient<Voucher>('/vouchers', { method: 'POST', body: input });
  },

  async deactivate(id: string): Promise<Voucher> {
    return apiClient<Voucher>(`/vouchers/${id}/deactivate`, { method: 'PUT' });
  },

  async remove(id: string): Promise<void> {
    await apiClient<void>(`/vouchers/${id}`, { method: 'DELETE' });
  },

  async stats(id: string): Promise<VoucherStats> {
    return withFallback(() => apiClient<VoucherStats>(`/vouchers/${id}/stats`), emptyStats);
  },

  async redemptions(id: string, page = 1, limit = 20): Promise<VoucherRedemptionList> {
    return withFallback(
      async () => {
        const res = await apiClientPaginated<VoucherRedemptionList['data']>(
          `/vouchers/${id}/redemptions${buildQuery({ page, limit })}`,
        );
        return { data: res.data, meta: res.meta };
      },
      emptyRedemptions,
    );
  },
};
